import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useEventListener } from '@hozana/hooks/useEventListener'
import { usePrevious } from '@hozana/hooks/usePrevious'

export type TScrollProps = {
  pageYOffset: number
  scrollDirection: 'toTop' | 'toBottom'
  hasDirectionChanged?: boolean
}

export type TUseScrollProps = {
  initDirection?: TScrollProps['scrollDirection']
}

export const useScroll = ({ initDirection = 'toTop' }: TUseScrollProps = {}): TScrollProps => {
  const [pageYOffset, setPageYOffset] = useState(__CLIENT__ ? window.pageYOffset : 0)
  const [scrollDirection, setScrollDirection] = useState(initDirection)
  const [hasDirectionChanged, setHasDirectionChanged] = useState(false)
  const prevPageYOffset = usePrevious(pageYOffset)

  const handleScroll = useDebouncedCallback(() => {
    if (__CLIENT__) {
      const scrollHeight = document.body.scrollHeight - document.body.clientHeight
      const isScrollDown = window.pageYOffset > pageYOffset || window.pageYOffset >= scrollHeight
      const newScrollDirection = isScrollDown ? 'toBottom' : 'toTop'

      if (scrollDirection !== newScrollDirection) {
        setScrollDirection(newScrollDirection)
        setHasDirectionChanged(true)
      } else {
        setHasDirectionChanged(false)
      }
      setPageYOffset(window.pageYOffset)
    }
  }, 100)

  useEventListener('scroll', handleScroll)

  // to reset scrollDirection on pageYOffset change and there is a scroll to the page top
  useEffect(() => {
    if (prevPageYOffset > pageYOffset && pageYOffset === 0) {
      setScrollDirection('toTop')
    }
  }, [pageYOffset, prevPageYOffset])

  return { pageYOffset, scrollDirection, hasDirectionChanged }
}
